import { AFORO_ZONES, AforoAreaId, AforoRect, AforoZone } from "./aforoConfig";

// Mobiliario reservable del modelo. Para cada zona con `elementos` en la
// configuración se localizan en el árbol del modelo las piezas que la
// amueblan: por prefijo de nombre ("pprimera_puestotrabajo") o, con "auto",
// las que caen enteras dentro de sus rectángulos. En las zonas que se
// reservan por puestos cada pieza es un BLOQUE (una mesa con sus sillas) con
// nombre propio ("Mesa 3") para que se pueda reservar hablando y una
// capacidad (el aforo de la zona repartido entre sus mesas). En salas y
// despachos las piezas solo sirven para teñir el espacio en la vista de
// reservas.
//
// Todo sale del modelo cargado en el visor, así que se descubre la primera
// vez que hace falta y se guarda; si el modelo aún no está, se devuelve vacío
// y se vuelve a intentar en la siguiente llamada.

export interface AforoBloque {
    /** `${zoneId}:${n}` (n empieza en 1 dentro de la zona). */
    id: string;
    /** "Mesa 3": nombre que dice la gente. */
    nombre: string;
    zoneId: string;
    /** La mesa. */
    dbId: number;
    /** La mesa y sus sillas (en el GLB cada silla es un elemento): lo que se tiñe. */
    dbIds: number[];
    /** Puestos que caben en el bloque: sus sillas si las tiene, o su parte del aforo. */
    puestos: number;
    center: { x: number; y: number; z: number };
}

/** Las sillas se llaman así en el modelo; se asignan a la mesa más cercana. */
const SILLA = "silla";
/** Distancia máxima (m) entre el centro de una silla y el de su mesa. */
const SILLA_RADIO = 2.5;

interface ZoneFurniture {
    dbIds: number[];
    bloques: AforoBloque[];
}

// Piezas que nunca son mobiliario aunque caigan dentro de una zona.
const EXCLUDE = [
    "luz",
    "puerta",
    "suelo",
    "pared",
    "techo",
    "plane",
    "ventana",
    "cristal",
    "muro",
    "detector",
    "tuberia",
    "ascensor",
    "logo",
    "object",
];

const inRect = (rect: AforoRect, x: number, z: number, tol = 0): boolean =>
    Math.abs(x - rect.x) <= rect.w / 2 + tol && Math.abs(z - rect.z) <= rect.d / 2 + tol;

const boxInsideRect = (rect: AforoRect, min: THREE.Vector3, max: THREE.Vector3): boolean => {
    const tol = Math.min(rect.w, rect.d) * 0.08;
    return inRect(rect, min.x, min.z, tol) && inRect(rect, max.x, max.z, tol);
};

class AforoBloques {
    private byArea: { [areaId: string]: { [zoneId: string]: ZoneFurniture } } = {};

    /** Descubre el mobiliario de una planta (una vez); false si el modelo no está listo. */
    ensure = (areaId: AforoAreaId): boolean => {
        if (this.byArea[areaId]) return true;
        const viewer = window.NOP_VIEWER;
        const model = viewer?.model;
        const tree = model?.getInstanceTree();
        const frags = model?.getFragmentList();
        if (!tree || !frags) return false;

        const zones = AFORO_ZONES.filter(
            (z) => z.area === areaId && z.elementos && z.rects && z.rects.length > 0
        );
        const found: { [zoneId: string]: Array<{ dbId: number; center: THREE.Vector3 }> } = {};
        zones.forEach((z) => {
            found[z.id] = [];
        });
        const sillas: Array<{ zoneId: string; dbId: number; center: THREE.Vector3 }> = [];
        if (zones.length === 0) {
            this.byArea[areaId] = {};
            return true;
        }

        tree.enumNodeChildren(
            tree.getRootId(),
            (dbId: number) => {
                const name = (tree.getNodeName(dbId) ?? "").toLowerCase();
                // Solo objetos con nombre: los hijos "Mesh" de cada objeto del GLB
                // no son piezas (un trozo de tabique dentro de un despacho se
                // teñía como un mueble).
                if (!name || name === "mesh" || EXCLUDE.some((e) => name.includes(e))) return;
                const bounds = new THREE.Box3();
                tree.enumNodeFragments(
                    dbId,
                    (fragId: number) => {
                        const fragBox = new THREE.Box3();
                        frags.getWorldBounds(fragId, fragBox);
                        bounds.union(fragBox);
                    },
                    true
                );
                if (bounds.isEmpty()) return;
                // A mano: el three.js del visor no tiene getCenter/getSize.
                const center = new THREE.Vector3()
                    .addVectors(bounds.min, bounds.max)
                    .multiplyScalar(0.5);
                const size = new THREE.Vector3().subVectors(bounds.max, bounds.min);
                if (size.x < 0.2 && size.z < 0.2) return; // motas: tiradores, enchufes...

                // Sillas de una zona de puestos: se guardan aparte y luego se
                // reparten entre sus mesas. En salas y despachos siguen el
                // reparto "auto" normal (amueblan la zona entera).
                if (name.includes(SILLA)) {
                    const zonaPuestos = zones.find(
                        (z) =>
                            z.reservable === "puesto" &&
                            z.rects!.some((r) => inRect(r, center.x, center.z))
                    );
                    if (zonaPuestos) {
                        sillas.push({ zoneId: zonaPuestos.id, dbId, center });
                        return;
                    }
                }

                // Por prefijo: la zona cuyo rectángulo contiene el centro. El
                // modelo usa el mismo prefijo para los escritorios de los
                // despachos y las mesas de las salas, así que una pieza con el
                // prefijo fuera de sus zonas no se fuerza a la más cercana:
                // pasa al reparto "auto" y amuebla la sala o el despacho.
                const byPrefix = zones.find(
                    (z) =>
                        z.elementos !== "auto" &&
                        name.startsWith(z.elementos!.toLowerCase()) &&
                        z.rects!.some((r) => inRect(r, center.x, center.z))
                );
                if (byPrefix) {
                    found[byPrefix.id].push({ dbId, center });
                    return;
                }
                // "auto": la pieza cae entera dentro de la zona.
                const auto = zones.find(
                    (z) =>
                        z.elementos === "auto" &&
                        z.rects!.some((r) => boxInsideRect(r, bounds.min, bounds.max))
                );
                if (auto) found[auto.id].push({ dbId, center });
            },
            true
        );

        const result: { [zoneId: string]: ZoneFurniture } = {};
        const counters: { [bloqueNombre: string]: number } = {};
        zones.forEach((zone) => {
            // Orden estable y legible: por filas (x) y dentro de la fila por z.
            const pieces = [...found[zone.id]].sort((a, b) => {
                const rowA = Math.round(a.center.x / 3);
                const rowB = Math.round(b.center.x / 3);
                return rowA !== rowB ? rowA - rowB : a.center.z - b.center.z;
            });
            const dbIds = pieces.map((p) => p.dbId);
            const bloques: AforoBloque[] = [];
            if (zone.reservable === "puesto" && zone.bloqueNombre && pieces.length > 0) {
                // El aforo de la zona repartido entre sus mesas; el resto, una
                // plaza más en las primeras, para que la suma sea exacta.
                const base = Math.floor(zone.aforoMaximo / pieces.length);
                const resto = zone.aforoMaximo - base * pieces.length;
                pieces.forEach((p, i) => {
                    counters[zone.bloqueNombre!] = (counters[zone.bloqueNombre!] ?? 0) + 1;
                    bloques.push({
                        id: `${zone.id}:${i + 1}`,
                        nombre: `${zone.bloqueNombre} ${counters[zone.bloqueNombre!]}`,
                        zoneId: zone.id,
                        dbId: p.dbId,
                        dbIds: [p.dbId],
                        puestos: Math.max(1, base + (i < resto ? 1 : 0)),
                        center: { x: p.center.x, y: p.center.y, z: p.center.z },
                    });
                });
                // Cada silla, a la mesa más cercana; con sillas, los puestos de
                // la mesa son sus sillas.
                sillas
                    .filter((s) => s.zoneId === zone.id)
                    .forEach((s) => {
                        let mejor: AforoBloque | undefined;
                        let mejorDist = SILLA_RADIO;
                        bloques.forEach((b) => {
                            const d = Math.hypot(b.center.x - s.center.x, b.center.z - s.center.z);
                            if (d < mejorDist) {
                                mejor = b;
                                mejorDist = d;
                            }
                        });
                        if (mejor) {
                            mejor.dbIds.push(s.dbId);
                            dbIds.push(s.dbId);
                        }
                    });
                bloques.forEach((b) => {
                    const numSillas = b.dbIds.length - 1;
                    // eslint-disable-next-line no-param-reassign
                    if (numSillas > 0) b.puestos = numSillas;
                });
            }
            result[zone.id] = { dbIds, bloques };
            if (pieces.length === 0) {
                console.warn(
                    `[aforo] sin mobiliario para la zona "${zone.nombre}" (${zone.elementos})`
                );
            }
        });
        this.byArea[areaId] = result;
        console.info(
            "[aforo] mobiliario reservable",
            Object.entries(result).map(([zoneId, f]) => ({
                zona: zoneId,
                piezas: f.dbIds.length,
                bloques: f.bloques.map((b) => `${b.nombre} (${b.puestos})`).join(", "),
            }))
        );
        return true;
    };

    private zoneOf = (zoneId: string): AforoZone | undefined =>
        AFORO_ZONES.find((z) => z.id === zoneId);

    /** Piezas del modelo que amueblan una zona (para teñirlas). */
    elementosDe = (zoneId: string): number[] => {
        const zone = this.zoneOf(zoneId);
        if (!zone || !this.ensure(zone.area)) return [];
        return this.byArea[zone.area][zoneId]?.dbIds ?? [];
    };

    /** Bloques (mesas) de una zona que se reserva por puestos; vacío si no tiene. */
    bloquesDe = (zoneId: string): AforoBloque[] => {
        const zone = this.zoneOf(zoneId);
        if (!zone || !this.ensure(zone.area)) return [];
        return this.byArea[zone.area][zoneId]?.bloques ?? [];
    };

    /** Todos los bloques de una planta, en su orden. */
    bloquesDeArea = (areaId: AforoAreaId): AforoBloque[] => {
        if (!this.ensure(areaId)) return [];
        return AFORO_ZONES.filter((z) => z.area === areaId).flatMap(
            (z) => this.byArea[areaId][z.id]?.bloques ?? []
        );
    };

    bloque = (bloqueId: string): AforoBloque | undefined => {
        const zoneId = bloqueId.split(":")[0];
        return this.bloquesDe(zoneId).find((b) => b.id === bloqueId);
    };

    /** ¿Tiene esta planta alguna zona con mobiliario para la vista de reservas? */
    hasFurniture = (areaId: AforoAreaId): boolean =>
        this.ensure(areaId) && Object.values(this.byArea[areaId]).some((f) => f.dbIds.length > 0);

    /**
     * "mesa 3", "la mesa tres"... → bloque. Busca en todas las plantas ya
     * descubiertas (y fuerza el descubrimiento de las que tienen bloques).
     */
    resolve = (query: string): AforoBloque | undefined => {
        const q = query
            .normalize("NFD")
            .replace(/[̀-ͯ]/g, "")
            .toLowerCase()
            .replace(/\s+/g, " ")
            .trim();
        if (!q) return undefined;
        const areas = Array.from(
            new Set(AFORO_ZONES.filter((z) => z.bloqueNombre).map((z) => z.area))
        );
        const words: { [w: string]: number } = {
            uno: 1,
            una: 1,
            dos: 2,
            tres: 3,
            cuatro: 4,
            cinco: 5,
            seis: 6,
            siete: 7,
            ocho: 8,
            nueve: 9,
            diez: 10,
            once: 11,
            doce: 12,
        };
        const normalized = q.replace(/\b([a-z]+)\b/g, (w) => (words[w] ? String(words[w]) : w));
        // Palabra completa: "mesa 1" no debe casar con "mesa 10".
        return areas
            .flatMap((a) => this.bloquesDeArea(a))
            .find((b) => new RegExp(`(^|\\s)${b.nombre.toLowerCase()}(\\s|$)`).test(normalized));
    };
}

export const aforoBloques = new AforoBloques();
